import React from 'react';
import { useTranslation } from 'react-i18next';
import { DataSource, PointOwner } from '../types';

/** One per `PointOwner`, so the control cannot express a state the model does not have. */
export type SourceChoice =
  | { kind: 'external'; sourceId: string }
  | { kind: 'mowazaf' }
  | { kind: 'notTracked' }
  | { kind: 'unset' };

interface SourceSelectProps {
  sources: DataSource[];
  owner: PointOwner;
  sourceId: string | null;
  disabled?: boolean;
  onChange: (choice: SourceChoice) => void;
  'data-testid'?: string;
}

// DOM option values, not domain identifiers. The three constant answers need something to put in the
// `value` attribute; none of them is a source id and none of them reaches the model.
const MOWAZAF_VALUE = '__mowazaf';
const NOT_TRACKED_VALUE = '__notTracked';
const NONE_VALUE = '';
const ADD_SOURCE_VALUE = '__addSource';

// A native select rather than the Radix combobox: this is a compact per-row control in a dense
// table, and the native control is the one that stays usable at 375px and in RTL.
const SourceSelect: React.FC<SourceSelectProps> = ({
  sources, owner, sourceId, disabled, onChange, 'data-testid': testId,
}) => {
  const { t } = useTranslation();

  const value = owner === 'notTracked'
    ? NOT_TRACKED_VALUE
    : owner === 'mowazaf'
      ? MOWAZAF_VALUE
      : sourceId ?? NONE_VALUE;

  const handleChange = (next: string) => {
    if (next === NOT_TRACKED_VALUE) return onChange({ kind: 'notTracked' });
    if (next === MOWAZAF_VALUE) return onChange({ kind: 'mowazaf' });
    if (next === NONE_VALUE) return onChange({ kind: 'unset' });
    return onChange({ kind: 'external', sourceId: next });
  };

  return (
    <select
      className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
      value={value}
      disabled={disabled}
      onChange={(e) => handleChange(e.target.value)}
      data-testid={testId}
    >
      <option value={NONE_VALUE}>{t('extInt.select.placeholder')}</option>
      <option value={MOWAZAF_VALUE}>{t('extInt.select.mowazaf')}</option>
      {sources.map((s) => (
        <option key={s.id} value={s.id}>{s.displayName}</option>
      ))}
      <option value={NOT_TRACKED_VALUE}>{t('extInt.select.notTracked')}</option>
      {/* A hint, not a control: an option cannot navigate, so it stays disabled. */}
      {sources.length === 0 && (
        <option value={ADD_SOURCE_VALUE} disabled>{t('extInt.select.addSource')}</option>
      )}
    </select>
  );
};

export default SourceSelect;
